import React, { useState, useRef, useEffect } from "react";
import { Modal, Checkbox, DatePicker, Spin } from "antd";
import {
  CloseOutlined,
  DownloadOutlined,
  CalendarOutlined,
  SearchOutlined,
  DownOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import FilterIcon from "@/assets/icons/FilterIcon";
import * as S from "./styles";

interface Company {
  id: number;
  companyName: string;
}

interface BillingDataExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  companies?: Company[];
  onExport?: (filters: ExportFilters) => void; // eslint-disable-line no-unused-vars
  isLoading?: boolean;
  error?: string | null;
}

export interface ExportFilters {
  companyIds?: number[];
  startDate?: string;
  endDate?: string;
  subscriptionTypes: number[];
}

interface SubscriptionType {
  value: number;
  label: string;
  badgeColor: string;
  textColor: string;
}

const subscriptionTypes: SubscriptionType[] = [
  {
    value: 2,
    label: "Single User",
    badgeColor: "#f1f5f9",
    textColor: "#314158",
  },
  {
    value: 5,
    label: "Single User (Premium)",
    badgeColor: "#f1f5f9",
    textColor: "#314158",
  },
  {
    value: 8,
    label: "Family Of 2",
    badgeColor: "#f3e8ff",
    textColor: "#8200db",
  },
  {
    value: 9,
    label: "Family Of 2 (Premium)",
    badgeColor: "#f3e8ff",
    textColor: "#8200db",
  },
  {
    value: 3,
    label: "Family of 4",
    badgeColor: "#fef3c6",
    textColor: "#bb4d00",
  },
  {
    value: 6,
    label: "Family of 4 (Premium)",
    badgeColor: "#fef3c6",
    textColor: "#bb4d00",
  },
  {
    value: 4,
    label: "Family of 6",
    badgeColor: "#d7efc7",
    textColor: "#44ad00",
  },
  {
    value: 7,
    label: "Family of 6 (Premium)",
    badgeColor: "#d7efc7",
    textColor: "#44ad00",
  },
];

const ALL_COMPANIES_VALUE = "all";

const BillingDataExportModal: React.FC<BillingDataExportModalProps> = ({
  isOpen,
  onClose,
  companies = [],
  onExport,
  isLoading = false,
  error = null,
}) => {
  const [selectedCompanies, setSelectedCompanies] = useState<
    Array<number | string>
  >([ALL_COMPANIES_VALUE]);
  const [startDate, setStartDate] = useState<string | undefined>(undefined);
  const [endDate, setEndDate] = useState<string | undefined>(undefined);
  const [selectedSubscriptionTypes, setSelectedSubscriptionTypes] = useState<
    number[]
  >([]);
  const [isCompanyDropdownOpen, setIsCompanyDropdownOpen] = useState(false);
  const [companySearchQuery, setCompanySearchQuery] = useState("");
  const [isStartDatePickerOpen, setIsStartDatePickerOpen] = useState(false);
  const [isEndDatePickerOpen, setIsEndDatePickerOpen] = useState(false);
  const companyDropdownRef = useRef<HTMLDivElement>(null);
  const startDatePickerRef = useRef<any>(null);
  const endDatePickerRef = useRef<any>(null);

  const handleCompanyCheckboxChange = (
    companyId: number | string,
    checked: boolean
  ) => {
    if (companyId === ALL_COMPANIES_VALUE) {
      if (checked) {
        setSelectedCompanies([ALL_COMPANIES_VALUE]);
      } else {
        setSelectedCompanies([]);
      }
      return;
    }
    if (checked) {
      // Remove "All companies" if it's selected
      const newSelection = selectedCompanies.filter(
        (id) => id !== ALL_COMPANIES_VALUE
      );
      setSelectedCompanies([...newSelection, companyId]);
    } else {
      setSelectedCompanies(selectedCompanies.filter((id) => id !== companyId));
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        companyDropdownRef.current &&
        !companyDropdownRef.current.contains(event.target as Node)
      ) {
        setIsCompanyDropdownOpen(false);
      }
    };

    if (isCompanyDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isCompanyDropdownOpen]);

  // Filter companies based on search query
  const filteredCompanies = companies.filter((company) =>
    company.companyName.toLowerCase().includes(companySearchQuery.toLowerCase())
  );

  // Get display text for selected companies
  const getCompanyDisplayText = () => {
    if (selectedCompanies.includes(ALL_COMPANIES_VALUE)) {
      return "All Companies";
    }
    if (selectedCompanies.length === 0) {
      return "Select Companies";
    }
    if (selectedCompanies.length === 1) {
      const company = companies.find((c) => c.id === selectedCompanies[0]);
      return company?.companyName || "Select Companies";
    }
    return `${selectedCompanies.length} companies selected`;
  };

  const handleStartDateChange = (date: any, dateString: string | string[]) => {
    const newStartDate = Array.isArray(dateString)
      ? dateString[0]
      : dateString || undefined;

    // Validate: start date cannot be after end date
    if (newStartDate && endDate) {
      const start = dayjs(newStartDate, "DD/MM/YYYY");
      const end = dayjs(endDate, "DD/MM/YYYY");
      if (start.isAfter(end)) {
        // Don't update if start date is after end date
        return;
      }
    }

    setStartDate(newStartDate);
  };

  const handleEndDateChange = (date: any, dateString: string | string[]) => {
    const newEndDate = Array.isArray(dateString)
      ? dateString[0]
      : dateString || undefined;

    // Validate: end date cannot be before start date
    if (newEndDate && startDate) {
      const start = dayjs(startDate, "DD/MM/YYYY");
      const end = dayjs(newEndDate, "DD/MM/YYYY");
      if (end.isBefore(start)) {
        // Don't update if end date is before start date
        return;
      }
    }

    setEndDate(newEndDate);
  };

  const handleStartDateButtonClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsStartDatePickerOpen(true);
    // Trigger click on the hidden DatePicker input to open the calendar
    setTimeout(() => {
      const pickerInput = startDatePickerRef.current?.input as HTMLInputElement;
      if (pickerInput) {
        pickerInput.click();
      }
    }, 10);
  };

  const handleEndDateButtonClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsEndDatePickerOpen(true);
    // Trigger click on the hidden DatePicker input to open the calendar
    setTimeout(() => {
      const pickerInput = endDatePickerRef.current?.input as HTMLInputElement;
      if (pickerInput) {
        pickerInput.click();
      }
    }, 10);
  };

  // Get display text for start date
  const getStartDateDisplayText = () => {
    if (!startDate) {
      return "Start Date";
    }
    // DatePicker returns dateString in DD/MM/YYYY format (based on format prop)
    // Return it directly since it's already formatted correctly
    return startDate;
  };

  // Get display text for end date
  const getEndDateDisplayText = () => {
    if (!endDate) {
      return "End Date";
    }
    // DatePicker returns dateString in DD/MM/YYYY format (based on format prop)
    // Return it directly since it's already formatted correctly
    return endDate;
  };

  const handleSubscriptionTypeChange = (value: number, checked: boolean) => {
    if (checked) {
      setSelectedSubscriptionTypes([...selectedSubscriptionTypes, value]);
    } else {
      setSelectedSubscriptionTypes(
        selectedSubscriptionTypes.filter((st) => st !== value)
      );
    }
  };

  const handleExport = () => {
    if (onExport) {
      // If "All companies" is selected, don't send companyIds (undefined means all)
      let companyIds: number[] | undefined;
      if (selectedCompanies.includes(ALL_COMPANIES_VALUE)) {
        companyIds = undefined;
      } else if (selectedCompanies.length > 0) {
        companyIds = selectedCompanies as number[];
      } else {
        companyIds = undefined;
      }

      onExport({
        companyIds,
        startDate,
        endDate,
        subscriptionTypes: selectedSubscriptionTypes,
      });
    }
  };

  const handleReset = () => {
    setSelectedCompanies([ALL_COMPANIES_VALUE]);
    setStartDate(undefined);
    setEndDate(undefined);
    setSelectedSubscriptionTypes([]);
  };

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={643}
      closeIcon={null}
      wrapClassName="billing-invoices-modal-wrap"
      getContainer={false}
      styles={{
        body: { padding: 0 },
      }}
      maskClosable={false}
    >
      <S.BillingExportWrapper>
        {/* Header */}
        <S.BillingExportHeader>
          <S.BillingExportHeaderTop>
            <S.BillingExportTitleSection>
              <S.BillingExportTitle>Billing Data Export</S.BillingExportTitle>
              <S.BillingExportDescription>
                Filter and export company data by date range and subscription
                type
              </S.BillingExportDescription>
            </S.BillingExportTitleSection>
            <S.BillingExportCloseButton onClick={onClose}>
              <CloseOutlined />
            </S.BillingExportCloseButton>
          </S.BillingExportHeaderTop>
        </S.BillingExportHeader>

        {/* Export Filters Card */}
        <S.BillingExportCard>
          <S.BillingExportCardHeader>
            <S.BillingExportCardTitle>
              <S.BillingExportCardIcon>
                <FilterIcon width={16} height={16} svgColor="#0A0A0A" />
              </S.BillingExportCardIcon>
              Export Filters
            </S.BillingExportCardTitle>
            <S.BillingExportCardDescription>
              Configure your export criteria.
            </S.BillingExportCardDescription>
          </S.BillingExportCardHeader>

          <S.BillingExportCardContent>
            {/* Company Name */}
            <S.BillingExportField>
              <S.BillingExportLabel>Company Name</S.BillingExportLabel>
              <S.BillingExportCompanyDropdownWrapper ref={companyDropdownRef}>
                <S.BillingExportCompanySelectButton
                  onClick={() =>
                    setIsCompanyDropdownOpen(!isCompanyDropdownOpen)
                  }
                  $isOpen={isCompanyDropdownOpen}
                >
                  <S.BillingExportCompanySelectText>
                    {getCompanyDisplayText()}
                  </S.BillingExportCompanySelectText>
                  <DownOutlined
                    style={{
                      fontSize: "12px",
                      color: "#0f172a",
                      transform: isCompanyDropdownOpen
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                      transition: "transform 0.2s ease",
                    }}
                  />
                </S.BillingExportCompanySelectButton>
                {isCompanyDropdownOpen && (
                  <S.BillingExportCompanyDropdown>
                    <S.BillingExportCompanySearchWrapper>
                      <S.BillingExportCompanySearchInput
                        placeholder="Search"
                        value={companySearchQuery}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setCompanySearchQuery(e.target.value)
                        }
                        prefix={<SearchOutlined />}
                      />
                    </S.BillingExportCompanySearchWrapper>
                    <S.BillingExportCompanyList>
                      <S.BillingExportCompanyItem>
                        <Checkbox
                          checked={selectedCompanies.includes(
                            ALL_COMPANIES_VALUE
                          )}
                          onChange={(e) =>
                            handleCompanyCheckboxChange(
                              ALL_COMPANIES_VALUE,
                              e.target.checked
                            )
                          }
                        />
                        <S.BillingExportCompanyBadge>
                          All Companies
                        </S.BillingExportCompanyBadge>
                      </S.BillingExportCompanyItem>
                      {filteredCompanies.map((company) => {
                        const isChecked = selectedCompanies.includes(
                          company.id
                        );
                        return (
                          <S.BillingExportCompanyItem key={company.id}>
                            <Checkbox
                              checked={isChecked}
                              onChange={(e) =>
                                handleCompanyCheckboxChange(
                                  company.id,
                                  e.target.checked
                                )
                              }
                            />
                            <S.BillingExportCompanyBadge>
                              {company.companyName}
                            </S.BillingExportCompanyBadge>
                          </S.BillingExportCompanyItem>
                        );
                      })}
                    </S.BillingExportCompanyList>
                  </S.BillingExportCompanyDropdown>
                )}
              </S.BillingExportCompanyDropdownWrapper>
            </S.BillingExportField>

            {/* Date Range */}
            <S.BillingExportDateRow>
              <S.BillingExportDateField>
                <S.BillingExportLabel>Start Date</S.BillingExportLabel>
                <S.BillingExportDateButtonWrapper>
                  <S.BillingExportDateSelectButton
                    onClick={handleStartDateButtonClick}
                    $isOpen={isStartDatePickerOpen}
                    type="button"
                  >
                    <CalendarOutlined
                      style={{ fontSize: "24px", color: "#0f172a" }}
                    />
                    <S.BillingExportDateSelectText>
                      {getStartDateDisplayText()}
                    </S.BillingExportDateSelectText>
                    <DownOutlined
                      style={{
                        fontSize: "12px",
                        color: "#0f172a",
                        transform: isStartDatePickerOpen
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                        transition: "transform 0.2s ease",
                      }}
                    />
                  </S.BillingExportDateSelectButton>
                  <S.BillingExportHiddenDatePicker>
                    <DatePicker
                      ref={startDatePickerRef}
                      placeholder="Start Date"
                      onChange={handleStartDateChange}
                      onOpenChange={(open) => {
                        setIsStartDatePickerOpen(open);
                      }}
                      format="DD/MM/YYYY"
                      open={isStartDatePickerOpen}
                      value={startDate ? dayjs(startDate, "DD/MM/YYYY") : null}
                      disabledDate={(current) => {
                        // Disable dates after end date if end date is selected
                        if (endDate) {
                          const end = dayjs(endDate, "DD/MM/YYYY");
                          return current && current.isAfter(end, "day");
                        }
                        return false;
                      }}
                    />
                  </S.BillingExportHiddenDatePicker>
                </S.BillingExportDateButtonWrapper>
              </S.BillingExportDateField>
              <S.BillingExportDateField>
                <S.BillingExportLabel>End Date</S.BillingExportLabel>
                <S.BillingExportDateButtonWrapper>
                  <S.BillingExportDateSelectButton
                    onClick={handleEndDateButtonClick}
                    $isOpen={isEndDatePickerOpen}
                    type="button"
                  >
                    <CalendarOutlined
                      style={{ fontSize: "24px", color: "#0f172a" }}
                    />
                    <S.BillingExportDateSelectText>
                      {getEndDateDisplayText()}
                    </S.BillingExportDateSelectText>
                    <DownOutlined
                      style={{
                        fontSize: "12px",
                        color: "#0f172a",
                        transform: isEndDatePickerOpen
                          ? "rotate(180deg)"
                          : "rotate(0deg)",
                        transition: "transform 0.2s ease",
                      }}
                    />
                  </S.BillingExportDateSelectButton>
                  <S.BillingExportHiddenDatePicker>
                    <DatePicker
                      ref={endDatePickerRef}
                      placeholder="End Date"
                      onChange={handleEndDateChange}
                      onOpenChange={(open) => {
                        setIsEndDatePickerOpen(open);
                      }}
                      format="DD/MM/YYYY"
                      open={isEndDatePickerOpen}
                      value={endDate ? dayjs(endDate, "DD/MM/YYYY") : null}
                      disabledDate={(current) => {
                        // Disable dates before start date if start date is selected
                        if (startDate) {
                          const start = dayjs(startDate, "DD/MM/YYYY");
                          return current && current.isBefore(start, "day");
                        }
                        return false;
                      }}
                    />
                  </S.BillingExportHiddenDatePicker>
                </S.BillingExportDateButtonWrapper>
              </S.BillingExportDateField>
            </S.BillingExportDateRow>

            {/* Subscription Types */}
            <S.BillingExportField>
              <S.BillingExportLabel>Subscription Types</S.BillingExportLabel>
              <S.BillingExportSubscriptionList>
                {subscriptionTypes.map((subscriptionType) => {
                  const isChecked = selectedSubscriptionTypes.includes(
                    subscriptionType.value
                  );
                  return (
                    <S.BillingExportSubscriptionItem
                      key={subscriptionType.value}
                    >
                      <Checkbox
                        checked={isChecked}
                        onChange={(e) =>
                          handleSubscriptionTypeChange(
                            subscriptionType.value,
                            e.target.checked
                          )
                        }
                      />
                      <S.BillingExportBadge
                        badgeColor={subscriptionType.badgeColor}
                        textColor={subscriptionType.textColor}
                      >
                        {subscriptionType.label}
                      </S.BillingExportBadge>
                    </S.BillingExportSubscriptionItem>
                  );
                })}
              </S.BillingExportSubscriptionList>
            </S.BillingExportField>

            {/* Action Buttons */}
            <S.BillingExportActions>
              <S.BillingExportButtonWrapper>
                <S.BillingExportButton
                  onClick={handleExport}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Spin
                        indicator={
                          <LoadingOutlined
                            style={{ fontSize: 14, color: "white" }}
                            spin
                          />
                        }
                        size="small"
                      />
                      Exporting...
                    </>
                  ) : (
                    <>
                      <DownloadOutlined />
                      Export
                    </>
                  )}
                </S.BillingExportButton>
                {error && <S.BillingExportError>{error}</S.BillingExportError>}
              </S.BillingExportButtonWrapper>
              <S.BillingExportResetButton
                onClick={handleReset}
                disabled={isLoading}
              >
                Reset Filters
              </S.BillingExportResetButton>
            </S.BillingExportActions>
          </S.BillingExportCardContent>
        </S.BillingExportCard>
      </S.BillingExportWrapper>
    </Modal>
  );
};

export default BillingDataExportModal;
